import React, { useState } from 'react';
import { DroneInfoOverlay } from '@src/features/drone/pages/drones-page/components/drone-info-overlay';
import { DroneEntity, DroneStatusType, useDroneProvider } from '@src/core/features';
import { SelectedDroneMarker } from '@src/features/drone/pages/drones-page/components/selected-drone-marker';
import { DroneMarkersList } from '@src/features/drone/pages/drones-page/components/drone-marker-list';
import { useDroneSelection } from '@src/features/drone/hooks/useDroneSelection';
import { useSelector } from 'react-redux';
import { selectHoveredDrone, selectSelectedDrone } from '@src/features/drone/redux';
import { useDroneRotations } from '@src/features/drone/hooks/useDroneRotations';
import { useFlyingDrones } from '@src/features/drone/hooks/useFlyingDrones';
import { useFlightProvider } from '@src/features/order/provider';
import { DronesMainMapProps } from './types';
import { useCloseOverlayOnDroneChange } from '@src/features/drone/hooks/useCloseOverlayOnDroneChange';
import { ArcPolyline } from '@src/core/controllers';
import { Marker } from '@react-google-maps/api';
import gohere from '@src/assets/gohere.svg';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';

export const DronesMainMap: React.FC <DronesMainMapProps>= (props) => {
  const { resetGoHere, position, removePosition, points = [], goTo } = props;
  const [clickedDrone, setClickedDrone] = useState<DroneEntity | null>(null);
  const selected = useSelector(selectSelectedDrone);

  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);
  const { drones: missionDrones } = useDroneProvider(DroneStatusType.MISSION);
  const { flightData } = useFlightProvider();
  const { rotations, previousFlights } = useDroneRotations(flightData);

  const flyingDrones = useFlyingDrones(missionDrones, previousFlights, flightData);
  const { selectedDrone, isOnMission } = useDroneSelection(selected, flyingDrones, missionDrones, idleDrones);
  const hoveredDrone = useSelector(selectHoveredDrone);
  useCloseOverlayOnDroneChange(selected, position, removePosition, clickedDrone, setClickedDrone);

  const { setCameraSource } = useCameraLayout();

  const [droneLocation, setDroneLocation] = React.useState<google.maps.LatLngLiteral | null>(null);

  React.useEffect(() => {
    if (selectedDrone) {
      setCameraSource({
        serialNumber: selectedDrone.serialNumber,
        resetGoHere,
        isEnRoute: isOnMission,
      });
    } else {
      setCameraSource(null);
    }
  }, [selectedDrone?.serialNumber, isOnMission, resetGoHere]);

  React.useEffect(() => {
    if (!flightData || !selectedDrone) {
      setDroneLocation(null);
      return;
    }

    if (flightData.serialNumber !== selectedDrone.serialNumber) {
      return;
    }

    const lat = Number(flightData.latitude);
    const lng = Number(flightData.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setDroneLocation(prev => {
        if (prev?.lat === lat && prev?.lng === lng) return prev;
        return { lat, lng };
      });
    }
  }, [flightData, selectedDrone]);


  const handleDroneClick = React.useCallback((drone: DroneEntity) => {
    setClickedDrone(drone);
  }, []);

  const handleCloseDroneInfo = React.useCallback(() => {
    setClickedDrone(null);
  }, []);

  return (
    <React.Fragment>
      
      {clickedDrone && (
        <DroneInfoOverlay
          drone={clickedDrone}
          position={{
            lat: Number(clickedDrone.latitude),
            lng: Number(clickedDrone.longitude),
          }}
          onClose={handleCloseDroneInfo}
          clickedDrone={clickedDrone}
          setClickedDrone={setClickedDrone}
        />
      )}

      {selectedDrone ? (
        <SelectedDroneMarker
          drone={selectedDrone}
          onClick={() => handleDroneClick(selectedDrone)}
          rotation={rotations[selectedDrone.serialNumber]}
          isOnMission={isOnMission}
        />
      ) : (
        <DroneMarkersList
          idleDrones={idleDrones}
          flyingDrones={flyingDrones}
          clickedDrone={clickedDrone}
          hoveredDrone={hoveredDrone}
          rotations={rotations}
          onDroneClick={handleDroneClick}
        />
      )}

      <ArcPolyline.error
        path={[
          {
            lat: droneLocation?.lat ?? Number(selectedDrone?.latitude),
            lng: droneLocation?.lng ?? Number(selectedDrone?.longitude),
          },
          ...points.map(p => p.position)
        ]}
      />

      {points.map(point => (
        <Marker
          key={point.id}
          position={point.position}
          icon={{
            url: gohere,
            scaledSize: new window.google.maps.Size(80, 40),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(10, 20),
          }}
        />
      ))}
    </React.Fragment>
  );
};